import { Box, ButtonGroup, IconButton, Tooltip } from '@cad-arc';
import React from 'react';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { AdditionalInfoType, AdditionalInfoView, Priority, PRO_QA_TYPES } from '../types';

export interface NarrativeActionMenuProps {
    info: AdditionalInfoView;
    currentUserId?: number;
    isPinned?: boolean;
    isScheduledEvent?: boolean;
    isBolo?: boolean;
    isFirstResponder?: boolean;
    isMergedEventNarrative?: boolean;
    /** Read-only surface: suppress every write action (edit/emphasize/pin/unpin/
     *  delete). "Go-To" (scroll-to) and "Open merged event" still render, since
     *  both are navigation (read) actions. Wired from the read-only narrative
     *  list (`isReadOnlyMode`) */
    readOnly?: boolean;
    onEdit?: () => void;
    onToggleEmphasis?: () => void;
    onPin?: () => void;
    onUnpin?: () => void;
    onScrollTo?: () => void;
    onOpenMergedEvent?: () => void;
    onDelete?: () => void;
    isDeleting?: boolean;
    isTogglingEmphasis?: boolean;
    isPinning?: boolean;
    isPinDisabledByCap?: boolean;
    pinDisabledTooltip?: string;
    /** Called when any button in the menu is clicked. Every button performs
     *  an action, and performing an action ends the row's keyboard-selected
     *  state — so this fires regardless of which button was pressed. */
    onActionClick?: () => void;
    /** Called on Escape while focus is within the menu — deselects the row
     *  and returns focus to the compose input. */
    onEscape?: () => void;
}

/** Every enabled (focusable) button within a menu container, in DOM order.
 *  Shared by this component's own left/right roving nav and by the row
 *  components' "Left/Right from the row itself enters the menu" handling
 *  (additional-info-row.tsx / pinned-narrative-row.tsx) — one query, not two
 *  copies of the same selector. */
export function getMenuButtons(menuContainer: Element | null): HTMLButtonElement[] {
    if (menuContainer == null) return [];
    return Array.from(menuContainer.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'));
}

/** True when `event` is a click synthesized by keyboard activation (Enter/
 *  Space, and switch-access/voice-control "activate" gestures) of a focused
 *  button, rather than a real mouse/touch click — the browser reports
 *  `detail === 0` for the former and `detail >= 1` for the latter. Used
 *  throughout the narrative row action controls (this menu, the inline
 *  editor's Save/Cancel, and the delete confirmation's Cancel) to decide
 *  whether a button press should also clear the row's keyboard-only
 *  selection — only a real pointer click should (rule 4 in the module doc on
 *  useNarrativeRowActions). */
export function isKeyboardActivatedClick(event: React.MouseEvent): boolean {
    return event.detail === 0;
}

/** Left/Right roving nav (WAI-ARIA toolbar pattern) between the enabled
 *  buttons within a container — moves focus to the previous/next button
 *  relative to whichever one currently has focus. No-ops on any other key,
 *  when focus isn't currently on one of the container's own buttons (e.g.
 *  focus is in a text editor elsewhere in the same container — Left/Right
 *  must move the text cursor there, not get hijacked), or when there's no
 *  next/previous button to move to. Callers handle Escape themselves before
 *  calling this — that behavior genuinely differs per caller (what to call,
 *  and whether to stopPropagation vs preventDefault), so it isn't folded in
 *  here. Shared by NarrativeActionMenu, DeleteNarrativeConfirmation, and
 *  NarrativeEditRow's own Cancel/Save pair. */
export function handleArrowKeyButtonNav(
    event: React.KeyboardEvent<Element>,
    container: Element
): void {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return;
    }
    const buttons = getMenuButtons(container);
    const currentIndex = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (currentIndex === -1) return;
    const nextButton = buttons[event.key === 'ArrowRight' ? currentIndex + 1 : currentIndex - 1];
    if (nextButton == null) return;
    event.preventDefault();
    nextButton.focus();
}

export function NarrativeActionMenu({
    info,
    currentUserId,
    isPinned = false,
    isScheduledEvent = false,
    isBolo = false,
    isFirstResponder = false,
    isMergedEventNarrative = false,
    readOnly = false,
    onEdit,
    onToggleEmphasis,
    onPin,
    onUnpin,
    onScrollTo,
    onOpenMergedEvent,
    onDelete,
    isDeleting = false,
    isTogglingEmphasis = false,
    isPinning = false,
    isPinDisabledByCap = false,
    pinDisabledTooltip,
    onActionClick,
    onEscape,
}: NarrativeActionMenuProps): React.JSX.Element | null {
    const {
        rollOutCadNarrativeFilteringAndFocusImprovementsEnabled,
        rollOutEditableInitialNarrativeEnabled,
        editAdditionalInfos,
        pinUnpinAdditionalInfos,
        deleteAdditionalInfos,
    } = useEnabledFeatures();

    // Controlled so the pin-cap tooltip closes on mouse-leave/blur. The action
    // menu is display:none when the row isn't hovered; a portaled Radix tooltip
    // left open would lose its trigger's box and reflow to the top-left corner.
    const [isPinTooltipOpen, setIsPinTooltipOpen] = React.useState(false);

    if (!rollOutCadNarrativeFilteringAndFocusImprovementsEnabled) {
        return null;
    }

    const canEdit = editAdditionalInfos ?? false;
    const canPinUnpin = pinUnpinAdditionalInfos ?? false;
    // Default true to match existing behavior in event-panel-additional-infos.tsx
    // and bolo-panel-additional-infos.tsx
    const canDelete = deleteAdditionalInfos ?? true;

    const isAuthor = currentUserId != null && currentUserId === info.authorUserId;
    const isUserType = info.type === AdditionalInfoType.USER;
    const isSystemType = info.type === AdditionalInfoType.SYSTEM;
    const isProQA = PRO_QA_TYPES.includes(info.type as AdditionalInfoType);
    const isHighPriority = info.priority === Priority.HIGH;
    // A scheduled event's initial narrative may have no stored priority (its blob
    // never set one); treat that unset case as STANDARD so the Emphasize action —
    // which keys off STANDARD — still renders. Scope this to scheduled events:
    // regular/BOLO narratives always have a persisted priority, so an unset value
    // there is unexpected and must NOT implicitly enable Emphasize.
    const isStandardPriority =
        info.priority === Priority.STANDARD || (isScheduledEvent && !info.priority);

    // RND-64298: the event narrative used to always be a synthesized pseudo-row whose id equaled
    // the agencyEventId (no real additional_infos row existed). Events created after the fix get a
    // real row instead — a genuinely distinct id from the shared id generator, so `id !== agencyEventId`
    // reliably tells the two apart without needing a new backend field. Legacy (un-migrated) event
    // narratives still 404 on edit, since there's no real row for them yet.
    const isLegacyEventNarrative = info.isEventNarrative && info.id === info.agencyEventId;
    const isRealNarrativeRow = !isLegacyEventNarrative;

    // A real row outlives the flag that created it. Once a department is switched back off, events
    // created while it was on keep their initial-narrative row, but the backend answers edits to it
    // with a 403 (AdditionalInfoService.enforceInitialNarrativeEditable) — so the affordance has to
    // disappear with the flag rather than fail on click.
    const isEditableNarrative =
        isRealNarrativeRow &&
        (!info.isEventNarrative || (rollOutEditableInitialNarrativeEnabled ?? false));

    // Legacy event narratives have no additional_info_event_links row to pin, so the request 404s.
    // Pinning goes through pinNarrative, which the initial-narrative edit guard does not cover, so
    // it is deliberately NOT gated on the rollout flag: a real row stays pinnable with the flag off.
    const isPinnableNarrative = isRealNarrativeRow;

    // Go-To (scroll-to) is a read action, so it survives read-only; every other
    // action mutates the narrative and is gated behind canModify.
    const canModify = !readOnly;

    const showScrollTo = isPinned && onScrollTo != null;
    const showEdit =
        canModify && canEdit && isAuthor && isUserType && isEditableNarrative && onEdit != null;
    const showEmphasize =
        canModify &&
        canEdit &&
        isAuthor &&
        isUserType &&
        isStandardPriority &&
        isEditableNarrative &&
        onToggleEmphasis != null;
    const showRemoveEmphasis =
        canModify &&
        canEdit &&
        isAuthor &&
        isUserType &&
        isHighPriority &&
        isEditableNarrative &&
        onToggleEmphasis != null;
    const showPin =
        canModify &&
        canPinUnpin &&
        !isPinned &&
        !isSystemType &&
        isPinnableNarrative &&
        onPin != null;
    const showUnpin =
        canModify &&
        canPinUnpin &&
        isPinned &&
        !isSystemType &&
        isPinnableNarrative &&
        onUnpin != null;
    // Merged events are a regular-event-only concept — explicitly gate on
    // entity type rather than relying solely on parentAgencyEventId being
    // absent/zeroed upstream for BOLO and scheduled events. Opening the
    // merged event is a navigation (read) action, so — like Go-To — it
    // survives read-only mode and isn't gated on canModify.
    const showOpenMergedEvent =
        isMergedEventNarrative && !isBolo && !isScheduledEvent && onOpenMergedEvent != null;
    const showDelete =
        canModify &&
        canDelete &&
        isAuthor &&
        isUserType &&
        !isScheduledEvent &&
        !info.isEventNarrative &&
        !isProQA &&
        onDelete != null;

    const hasAnyAction =
        showScrollTo ||
        showEdit ||
        showEmphasize ||
        showRemoveEmphasis ||
        showPin ||
        showUnpin ||
        showOpenMergedEvent ||
        showDelete;

    if (!hasAnyAction) {
        return null;
    }

    const buttonSize = isFirstResponder ? 'lg' : 'sm';

    const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === 'Escape') {
            event.preventDefault();
            onEscape?.();
            return;
        }
        handleArrowKeyButtonNav(event, event.currentTarget);
    };

    const handleMenuClick = (event: React.MouseEvent<HTMLDivElement>): void => {
        if (!(event.target as HTMLElement).closest('button')) {
            return;
        }
        if (isKeyboardActivatedClick(event)) {
            // This click still bubbles up to the row's own onClick
            // (handleRowDeselect) unless stopped here — the menu is a DOM
            // descendant of the row, so a keyboard click on a menu button
            // would otherwise deselect via the row's unconditional onClick
            // even though onActionClick itself was skipped above.
            event.stopPropagation();
            return;
        }
        onActionClick?.();
    };

    const pinButton = (
        <IconButton
            aria-label="Pin"
            data-testid="NARRATIVE_ACTION_PIN"
            icon="PinOutline"
            size={buttonSize}
            variant="ghost"
            onClick={onPin}
            isDisabled={isPinning || isPinDisabledByCap}
        />
    );

    return (
        <ButtonGroup
            isAttached
            className="narrative-action-menu"
            role="toolbar"
            aria-label="Narrative actions"
            data-testid="NARRATIVE_ACTION_MENU"
            onClick={handleMenuClick}
            onKeyDown={handleMenuKeyDown}
        >
            {showScrollTo && (
                <IconButton
                    aria-label="Scroll to"
                    data-testid="NARRATIVE_ACTION_SCROLL_TO"
                    icon="Crosshairs"
                    size={buttonSize}
                    variant="ghost"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        onScrollTo?.();
                        // Scrolling elsewhere doesn't move DOM focus, so without this
                        // the clicked button (and thus this row) stays :focus-within
                        // after the click — the menu reveal rule below keys off
                        // :hover OR :focus-within, so this row's menu would stay
                        // visible indefinitely, showing two rows' menus at once as
                        // soon as the mouse hovers another row.
                        event.currentTarget.blur();
                    }}
                />
            )}
            {showEdit && (
                <IconButton
                    aria-label="Edit"
                    data-testid="NARRATIVE_ACTION_EDIT"
                    icon="Edit"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onEdit}
                />
            )}
            {showEmphasize && (
                <IconButton
                    aria-label="Emphasize"
                    data-testid="NARRATIVE_ACTION_EMPHASIZE"
                    icon="ExclamationPoint"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onToggleEmphasis}
                    isDisabled={isTogglingEmphasis}
                />
            )}
            {showRemoveEmphasis && (
                <IconButton
                    aria-label="Remove emphasis"
                    data-testid="NARRATIVE_ACTION_REMOVE_EMPHASIS"
                    icon="ExclamationPointOff"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onToggleEmphasis}
                    isDisabled={isTogglingEmphasis}
                />
            )}
            {showPin &&
                (pinDisabledTooltip ? (
                    <Tooltip
                        content={pinDisabledTooltip}
                        side="top"
                        hasArrow
                        isOpen={isPinTooltipOpen}
                        onOpenChange={setIsPinTooltipOpen}
                    >
                        <Box
                            as="span"
                            display="inline-flex"
                            onMouseEnter={() => setIsPinTooltipOpen(true)}
                            onMouseLeave={() => setIsPinTooltipOpen(false)}
                            onFocus={() => setIsPinTooltipOpen(true)}
                            onBlur={() => setIsPinTooltipOpen(false)}
                        >
                            {pinButton}
                        </Box>
                    </Tooltip>
                ) : (
                    pinButton
                ))}
            {showUnpin && (
                <IconButton
                    aria-label="Unpin"
                    data-testid="NARRATIVE_ACTION_UNPIN"
                    icon="PinOutlineOff"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onUnpin}
                    isDisabled={isPinning}
                />
            )}
            {showOpenMergedEvent && (
                <IconButton
                    aria-label="Open merged event"
                    data-testid="NARRATIVE_ACTION_OPEN_MERGED_EVENT"
                    icon="OpenNewWindow"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onOpenMergedEvent}
                />
            )}
            {showDelete && (
                <IconButton
                    aria-label="Delete"
                    data-testid="NARRATIVE_ACTION_DELETE"
                    icon="TrashCan"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onDelete}
                    isDisabled={isDeleting}
                />
            )}
        </ButtonGroup>
    );
}
